// **Per-session, idempotent, demand-driven restore** (`client-app` "Session restore is per-session,
// idempotent and demand-driven"; `rounds-refresh-restore` tasks 3.4).
//
// ## The defect this closes
//
// Restore used to be a **mount effect** guarded by a once-only `bootedRef`. Two properties came from
// that shape and only one of them was wanted:
//
//   * *Wanted*: exactly one restore attempt per page load, so a StrictMode double-invoke does not mint
//     two memberships and abandon one.
//   * *Unwanted*: the attempt is tied to **which route the browser happened to mount on**. The boot
//     restored the tournament session and nothing else, so a player who booted on the practice path
//     had no tournament restore — and because the guard is once-only, navigating to `/` afterwards
//     could not run one either. They were told they had no membership while a perfectly valid
//     credential sat in storage, and only a reload fixed it.
//
// Keying the guard to the **session** instead of to the boot keeps the first property and drops the
// second. Any consumer that needs a session asks for it; the first ask runs the attempt and every
// other ask joins it.
//
// ## Why coalescing, and not "drop the second caller"
//
// A demand means "make sure this session is restored", not "start a restore". One already being in
// flight satisfies that request exactly, so the second caller awaits the first rather than being
// refused — a refusal would make the caller believe the session was unavailable when it was merely
// still arriving. This is the reading `RoundsController`'s `runRestore` already took for its Retry
// control; this generalises it rather than inventing it.

/**
 * Where a session's single restore attempt has got to.
 *
 * `failed` is deliberately distinct from `absent`. `absent` means nothing has been tried, so a demand
 * should try; `failed` means an attempt ran and could not decide, so the credential is kept and the
 * player is offered Retry rather than being silently re-asked on every render.
 */
export type SessionRestoreState = 'absent' | 'in-flight' | 'restored' | 'failed';

/** What one attempt reports. `restored` and `failed` are settled; see {@link SessionRestoreState}. */
export type SessionAttemptOutcome = 'restored' | 'failed' | 'absent';

export interface SessionDemand {
  /**
   * Ensure this session has been restored, running at most one attempt.
   *
   * Idempotent in the sense that matters: however many consumers call it, and however many times,
   * exactly one attempt is issued per settled outcome. Callers arriving while one is in flight await
   * that one.
   */
  demand(): Promise<void>;
  /**
   * The current state — the guard's own answer about this session.
   *
   * Kept deliberately, though no screen renders from it yet (implementation review **F6**): it is the
   * observable this seam's contract is asserted through, and it is only ever truthful if **every**
   * restore goes through {@link demand} and {@link reset}. That is why the join hand-back in
   * `RoundsController` calls `reset(); demand()` rather than the restore directly — a restore run
   * around the guard would leave this reporting an answer that is no longer true.
   */
  state(): SessionRestoreState;
  /**
   * Discard the settled outcome so the next demand attempts again.
   *
   * The **Retry** control's, and a join's: both mean "the previous answer is no longer the answer".
   * Deliberately not automatic — a boot that silently re-asks is what cost two failed requests on
   * every single page load.
   */
  reset(): void;
}

/**
 * Wraps one restore attempt in the single-flight guard above.
 *
 * `attempt` is expected not to throw — the shipped restore classifies its own failures — but a throw
 * is still handled rather than left to wedge the guard shut: it settles as `failed` and the in-flight
 * slot is cleared, so Retry works afterwards.
 */
export function createSessionDemand(
  attempt: () => Promise<SessionAttemptOutcome>,
): SessionDemand {
  let state: SessionRestoreState = 'absent';
  /**
   * Whether the one attempt has run.
   *
   * Tracked separately from `state` because an attempt that finds **nothing to restore** settles back
   * on `absent` — the honest description of the session — while still having been attempted. Gating
   * on `state` alone would therefore re-attempt on every demand for exactly the player who has no
   * credential, which is the one case that must stay quiet: `client-home` requires that arriving at a
   * route with no stored credential issues no request at all, and a re-asking guard would turn one
   * silent no-op into one per consumer.
   */
  let attempted = false;
  let inFlight: Promise<void> | null = null;

  return {
    state: () => state,
    reset: () => {
      // Only a *settled* outcome is discarded. An attempt still in flight keeps its slot, so a reset
      // can never produce two concurrent attempts.
      if (inFlight === null) {
        attempted = false;
        state = 'absent';
      }
    },
    demand(): Promise<void> {
      if (inFlight !== null) {
        // **Coalesce, don't refuse.** A demand means "make sure this session is restored", and one
        // already is being; awaiting it satisfies the request without a second round trip.
        return inFlight;
      }
      if (attempted) {
        return Promise.resolve();
      }
      attempted = true;
      state = 'in-flight';
      const running = (async () => {
        try {
          state = await attempt();
        } catch {
          // The shipped restore classifies its own failures and should not throw. Handled anyway
          // rather than left to wedge the guard shut: `inFlight` clears below either way, so Retry
          // still works.
          state = 'failed';
        } finally {
          inFlight = null;
        }
      })();
      inFlight = running;
      return running;
    },
  };
}
